import React, { useState, useEffect } from "react";
import { Helmet } from 'react-helmet-async';
import { useLocation, useNavigate } from 'react-router-dom';
import * as Steps from './steps/index';
import ResultVersionControl from "./ResultVersionControl";
import {
  setBasicInfo,
  getSessionStorageItem,
  SESSION_STORAGE_ACCOUNT_KEY,
  getBasicInfo,
  SESSION_STORAGE_BASIC_INFO_KEY,
  SESSION_STORAGE_FINAL_EXPENSE_TERM_KEY,
  SESSION_STORAGE_FINAL_EXPENSE_HEALTH_KEY,
  SESSION_STORAGE_FINAL_EXPENSE_SORT_FILTER_KEY,
} from '../../../sessionStorage';
import { saveSessionEvent } from "../../../session";
import { format } from 'date-fns';
import { EasyStepsSchedule, EasyStepsConfirmation } from "./Helpers/Constant";
import { InsurancePlan } from "../FinalExpense/Helpers/Constant";
import { ChoiceConvenienceData } from "../Common/Appointment/CustomAppointmentData";
import { JourneyTypes } from "../../Helpers/Constants/JourneyTypes";
import { PRODUCT_URLS } from "../../Helpers/Constants/Constant";
import './FinalExpense.scss';

export default function FinalExpense() {
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => { saveSessionEvent() }, [location])

  const path = location ? location.pathname.split("/").pop() : null;

  if (path === 'ns') {
    //clear product session
    sessionStorage.removeItem(SESSION_STORAGE_FINAL_EXPENSE_TERM_KEY);
    sessionStorage.removeItem(SESSION_STORAGE_FINAL_EXPENSE_HEALTH_KEY);
    sessionStorage.removeItem(SESSION_STORAGE_FINAL_EXPENSE_SORT_FILTER_KEY);
    sessionStorage.removeItem(SESSION_STORAGE_BASIC_INFO_KEY);
  }

  const sessionStorageFormState = getSessionStorageItem(SESSION_STORAGE_FINAL_EXPENSE_TERM_KEY);
  const sessionStorageFinalExpenseSettings = getSessionStorageItem(SESSION_STORAGE_FINAL_EXPENSE_SORT_FILTER_KEY);

  const prefill = location.search && location.search.toLowerCase().includes('prefill');
  const basicInfo = getBasicInfo();

  const newFormState = {
    currentStep: 1,
    pages: {
      quoter: Steps.Quoter.DefaultFormState(basicInfo),
      selectAppointment: Steps.SelectAppointment.DefaultFormState(prefill, basicInfo),
    },
  };

  const initialFormState = sessionStorageFormState || newFormState;

  if (sessionStorageFinalExpenseSettings) {
    initialFormState.pages.quoter.coverageAmount = sessionStorageFinalExpenseSettings.term.coverageAmount;
    initialFormState.pages.quoter.coverageLength = sessionStorageFinalExpenseSettings.term.termLength;
    initialFormState.pages.quoter.healthClass = sessionStorageFinalExpenseSettings.term.healthClass;
  }

  const [formState, setFormState] = useState(initialFormState);
  const journeyType = formState.pages.quoter.isSelectOrTalk === "contactSpecialist" ? JourneyTypes.CONTACT_A_SPECIALIST : JourneyTypes.APP_REQUEST;

  // When coming from the landing page form, skip to step 2
  useEffect(() => {
    if (location.search.includes('landing') && formState.currentStep === 1) {
      setFormState({ ...formState, currentStep: 2 });
    }
  }, []);

  // update the formState when the url path changes the step
  useEffect(() => {
    if (!path) {
      return;
    }

    const nextStep = steps.find(s => s.path === path);

    // prevent submitted app alterations
    if (!sessionStorageFormState && formState.currentStep === steps.length) {

      // if they are backing or directly going to another step, send them to product root
      if (formState.currentStep !== nextStep.step) {
        navigate('/final-expense');
      }
    }

    if (!nextStep) {
      return;
    }
    const newFormState = { ...formState, currentStep: nextStep.step };
    setFormState(newFormState);
  }, [path]);

  const setEmailPrefill = (basicInfo) => {
    setBasicInfo(basicInfo);
  };
  
  const setQuoteEmail = () => {
    const basicInfo = getBasicInfo();
    onFieldChange("selectAppointment", "email", basicInfo.email);
  };

  const onNext = () => {
    const nextStep = steps.find(s => s.step === formState.currentStep + 1);

    if (!nextStep) {
      throw new Error("onNext did not find a next step");
    }

    const newFormState = { ...formState, currentStep: nextStep.step };

    setFormState(newFormState);
    sessionStorage.setItem(SESSION_STORAGE_FINAL_EXPENSE_TERM_KEY, JSON.stringify(newFormState));

    navigate(`/final-expense/${nextStep.path}`);
    window.scroll(0, 0);
  };

  const onBack = () => {
    let backStep = steps.find(s => s.step === formState.currentStep - 1);
    if (formState.currentStep === 3) {backStep = 1;} // Select appointment should go back to results

    if (!backStep) {
        navigate(`/landing/final-expense`);
        return;
    }  

    const newFullFormState = { ...formState, currentStep: backStep.step };

    setFormState(newFullFormState);
    sessionStorage.setItem(SESSION_STORAGE_FINAL_EXPENSE_TERM_KEY, JSON.stringify(newFullFormState));
    navigate(`/final-expense/${backStep.path}`);
    window.scroll(0, 0);
  };

  const onFieldChange = (step, field, value) => {
    setFormState({ ...formState, pages: { ...formState.pages, [step]: { ...formState.pages[step], [field]: value } } });
  };

  const calculatePercentage = (totalStep, step, categoryStep, currentCateTotalStep) => {
    const stepPer = ((step - 1) / totalStep * 100);
    const cateCount = currentCateTotalStep === 1 ? 1 : (((categoryStep - 0.5) / currentCateTotalStep) * (100 / totalStep));
    return stepPer + cateCount;
  };

  const steps = [
    {
      step: 1,
      path: 'quote-results',
      progressTitle: 'Getting Started',
      component: <ResultVersionControl 
        result={{
          version: null,
          productType: "FinalExpense",
          formStatePagesQuoter: formState.pages.quoter,
          fullFormState: formState,
          basicInfo: basicInfo,
          onNext: onNext,
          onBack: onBack,
          onFieldChange: (field, value) => onFieldChange('quoter', field, value),
          setFormState: (coverageAmount, termLength, healthClass) =>
            setFormState({
              ...formState,
              pages: {
                ...formState.pages,
                quoter: {
                  ...formState.pages.quoter,
                  coverageAmount: coverageAmount,
                  term: termLength,
                  healthClass: healthClass
                }
              },
            }),
          onMultiFieldChange: (fs) => {
            setFormState(fs);
            sessionStorage.setItem(SESSION_STORAGE_FINAL_EXPENSE_TERM_KEY, JSON.stringify(fs));
          },
          setEmailPrefill: setEmailPrefill,
          setQuoteEmail: setQuoteEmail,
        }}
      />,
      hideProgressTracker: true,
      showContainer: false,
      fullWidth: true,
    },
    {
      step: 2,
      path: 'select-appointment',
      progressTitle: 'Schedule your application',
      title: "FINAL EXPENSE INSURANCE / SCHEDULE CALL",
      component: (
        <Steps.SelectAppointment
          productUrl={PRODUCT_URLS.FE}
          formState={formState.pages.selectAppointment}
          quoter={formState.pages.quoter}
          data={EasyStepsSchedule}
          journeyType={journeyType}
          title="Easy Next Steps"
          sessionKey={SESSION_STORAGE_FINAL_EXPENSE_HEALTH_KEY}
          choiceConvenienceTitle="Choice and Convenience"
          choiceConvenienceData={ChoiceConvenienceData}
          basicInfo={basicInfo}
          onFieldChange={(field, value) => onFieldChange('selectAppointment', field, value)}
          onNext={onNext}
          setMultiField={(firstName, lastName, email, configurableQuestions) => {
            setFormState({
              ...formState,
              pages: {
                ...formState.pages,
                selectAppointment: { ...formState.pages.selectAppointment, firstName: firstName, lastName: lastName, email: email, configurableQuestions: configurableQuestions }
              }
            });
            sessionStorage.setItem(SESSION_STORAGE_FINAL_EXPENSE_TERM_KEY, JSON.stringify(formState));
          }}
          fullFormState={formState}
        />
      ),
      hideProgressTracker: true,
      showContainer: false,
      fullWidth: true,
    },
    {
      step: 3,
      path: 'thank-you',
      progressTitle: 'Schedule your application',
      title: "FINAL EXPENSE INSURANCE / CALL SCHEDULED",
      component: (
        <Steps.ScheduleAppointment
          quoter={formState.pages.quoter}
          appointment={formState.pages.selectAppointment}
          data={EasyStepsConfirmation}
          isFinalExpense={true}
          insurancePlan={InsurancePlan}
          title="What happens next"
        />
      ),
      hideProgressTracker: true,
      showContainer: false,
      fullWidth: true,
    },

  ];

  const currentStepObj = steps.find(s => s.step === formState.currentStep) || steps[0];
  const categories = steps.filter((o) => o.categoryNumber === currentStepObj.categoryNumber);
  const totalCategoriesStep = Math.max.apply(Math, categories.map((o) => {
    return o.categoryStep;
  }));

  return (
    <div>
      <Helmet>
        <title>{`Final Expense Insurance - ${currentStepObj.progressTitle}`}</title>
      </Helmet>
        <div className="finalExpense pb-3">
          {currentStepObj.component}
        </div>
    </div>
  );
}
